import React, { useCallback, useEffect, useMemo } from 'react';
import type { StationeryTemplate } from '../../../DesignsGallery.types';
import type { TagVariant } from '../../../../GalleryTile';
import { AssetPreview, GalleryTile, GalleryTileWithRef } from '../../../../GalleryTile';
import { templateToTitle } from '@apps/card/utils';
import { maybeApplyCallback } from '@shared/utils/maybeApplyCallback';
import { Box } from '@withjoy/joykit';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { desktopAspect, mobileAspect, tileRowGap, treatmentDesktopAspect, treatmentMobileAspect } from '../../CardsConfig';
import { ProductPrices } from './ProductPrices/ProductPrices';
import type { CardFormat } from '../../../../ProductDetailsPage/constants';
import { useItemImpressionsTelemetry } from '../../ItemImpressionsTelemetry';
import { useInView } from '@shared/hooks/useInView';
import { useNearViewport } from '@shared/hooks/useNearViewport';
import { CardThumbnail } from './CardThumbnail';
import { CardColorSwatches } from '@apps/card/components/CardColorSwatches';
import { useColorways } from '@apps/card/hooks/useColorways';
import { useEventDesignThemeId } from '../../../useEventDesignThemeId';
import { useGalleryColorwayOrder } from '../../../GalleryColorwayOrderContext';
import { useGallerySearchScore } from '../../../GallerySearchScoresContext';
import { SearchScoreDebug } from './SearchScoreDebug';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';

export type CardTileSource = 'shop' | 'favoritesGallery';

type CardTileProps = Readonly<{
  data: StationeryTemplate & { type: 'template'; matchingTagCount?: number; colorwayOverride?: string };
  eventId?: string;
  onFavoriteClick?: (template: StationeryTemplate) => Promise<void>;
  hideTag?: boolean;
  hideTilePrice?: boolean;
  cardTileSource: CardTileSource;
  preferredFormat?: CardFormat;
}>;

export const CardTile = (props: CardTileProps) => {
  const { isSuperAdmin } = useAuth();
  const { tq } = useTranslation('stationery');
  const isMobile = useIsMobileScreen();
  const { isPublicShopRoute } = useCardsRouterContext();

  const { cardTileSource, data, hideTag, hideTilePrice, preferredFormat } = props;
  const { category, approved, format, enabled } = data;

  const { buttonInteracted, markTemplateAsFavorite } = useCardTelemetry();

  // Keyed by template `id` to match `getGroupKey` in useSearchFilteredGalleryItems.
  const searchScore = useGallerySearchScore(data.id);

  const { goToProductDetailsPage, eventHandle } = useCardsRouterContext();

  const handleOnViewTemplatePdpClick = useEventCallback((template: StationeryTemplate) => {
    buttonInteracted({
      name: 'designsGallery_tileClicked',
      themeId: template.themeId,
      stationeryTemplateCategory: template.category,
      format: template.format,
      isPremiumDesign: template.premium,
    });
    goToProductDetailsPage('wedding', category, template.themeId, template.id, 'push', preferredFormat, selectedId ? selectedId : undefined);
  });

  const isPremium = data.premium || format === 'paper';

  const tagVariant: TagVariant | undefined = enabled
    ? approved
      ? // No tag if enabled
        undefined
      : 'comingSoon'
    : 'disabled';

  const priceElement = hideTilePrice ? undefined : <ProductPrices template={data} />;

  const handleOnToggleFavorite = useEventCallback((isFavorite: boolean) => {
    markTemplateAsFavorite({ themeId: data.themeId, templateCategory: data.category, isFavorite, source: cardTileSource });
  });

  const { setImpression } = useItemImpressionsTelemetry();

  // Threshold set to 0.6 because:
  // a) When scrolling up, the image of the tile is fully revealed by 0.6. No need to wait until 1.0.
  // b) If 1.0, the right edge of tiles isn't counting as "in view". Perhaps we're off by a pixel in layout.
  const { ref: setInViewEl, inView } = useInView<HTMLDivElement>({ threshold: 0.6 });
  useEffect(() => {
    if (inView) setImpression(data.id);
  }, [data.id, setImpression, inView]);

  // Defer CardThumbnail mounting until the tile is near the viewport.
  const { ref: nearViewportRef, hasBeenNear } = useNearViewport({ preloadViewports: 0.4 });

  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      setInViewEl(el);
      nearViewportRef(el as Element | null);
    },
    [setInViewEl, nearViewportRef],
  );

  const eventDesignThemeId = useEventDesignThemeId(eventHandle);

  // While a search term or a `Colors` filter is narrowing the gallery, order
  // swatches by how well each colorway matched (best first) and pre-select the
  // top one, so the thumbnail renders in it. Swatches are never hidden —
  // colorways that didn't rank keep their catalog order after the ranked ones.
  // Otherwise (or outside the designs gallery, e.g. favorites) the order is
  // undefined and the tile is untouched.
  const { colorwayIds: colorwayOrder, source: colorwayOrderSource } = useGalleryColorwayOrder(data.themeId);
  const pageFront: PageJson | undefined = data.themeJSON?.card?.front;
  const { sortedOptions, bestMatchId } = useMemo(() => {
    const allOptions = pageFront?.colorways?.options;
    if (!allOptions || !colorwayOrder?.length) return { sortedOptions: allOptions, bestMatchId: undefined };
    const rank = new Map(colorwayOrder.map((id, index) => [id, index]));
    // Stable sort: ranked colorways first (in match order), unranked keep their relative order at the end.
    const sorted = [...allOptions].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
    const bestMatchId = colorwayOrder.find(id => allOptions.some(o => o.id === id));
    return { sortedOptions: sorted, bestMatchId };
  }, [pageFront, colorwayOrder]);

  // `bestMatchId` comes from an active search or color filter; `data.colorwayOverride`
  // from the gallery-order flag repeating a template with a colorway. They never
  // coexist — the flag's expansion is skipped while either is narrowing the gallery
  // (see `DesignsGallery`) — so the order here is a formality.
  const colorwayOverride = bestMatchId ?? data.colorwayOverride;
  const { options, selectedId, setSelectedId, preloadImages } = useColorways(pageFront, 'small', colorwayOverride);

  // Digital-only designs use a Design Service `design.id`, not a stationery-template
  // id, so the favorite mutation can't persist them — hide the badge rather than show
  // a control that silently no-ops.
  const isDigitalOnly = format === 'digital';

  const renderedOptions = sortedOptions ?? options;

  const handleColorwaySelect = useEventCallback((id: string) => {
    buttonInteracted({
      name: 'colorwaySwatchClicked',
      source: cardTileSource,
      themeId: data.themeId,
      templateId: data.id,
      stationeryTemplateCategory: data.category,
      colorwayId: id,
      previousColorwayId: selectedId,
      swatchIndex: renderedOptions?.findIndex(option => option.id === id) ?? -1,
      totalColorways: renderedOptions?.length ?? 0,
      isSearchOrdered: colorwayOrderSource === 'search' && !!colorwayOrder?.length,
      colorwayOrderSource: colorwayOrder?.length ? colorwayOrderSource : undefined,
    });
    setSelectedId(id);
  });

  const handleColorwayOverflowClick = useEventCallback((hiddenCount: number) => {
    buttonInteracted({
      name: 'designsGallery_colorwayOverflowClicked',
      themeId: data.themeId,
      stationeryTemplateCategory: data.category,
      hiddenCount,
    });
    handleOnViewTemplatePdpClick(data);
  });

  const badges = (
    <>
      {isPremium && <GalleryTile.PremiumBadge />}
      {!isDigitalOnly && (
        <GalleryTile.FavoriteBadge
          eventId={props.eventId}
          onToggle={handleOnToggleFavorite}
          template={data}
        />
      )}
      {data.isFoil && <GalleryTile.FoilBadge />}
    </>
  );

  const brow = isPublicShopRoute && category === StationeryTemplateCategoryEnum.saveTheDate ? tq`Save the Date Cards` : undefined;

  const aspectRatio = hideTilePrice ? (isMobile ? treatmentMobileAspect : treatmentDesktopAspect) : isMobile ? mobileAspect : desktopAspect;

  return enabled || isSuperAdmin ? (
    <GalleryTileWithRef
      ref={ref}
      aspectRatio={aspectRatio}
      alignContent="start"
      rowGap={tileRowGap}
      onMouseEnter={preloadImages}
      onClick={maybeApplyCallback(!!(approved || isSuperAdmin), () => handleOnViewTemplatePdpClick(data))}
    >
      {eventDesignThemeId === data.themeId && (
        <Box
          typographyVariant="label1"
          fontWeight={600}
          position="absolute"
          paddingX={4}
          paddingY={2}
          left={5}
          top={4}
          backgroundColor="mono14"
          color="white"
          userSelect="none"
          borderRadius={5}
          zIndex={1}
        >
          {tq`Matches Your Site`}
        </Box>
      )}
      {hasBeenNear ? (
        <CardThumbnail
          template={data}
          selectedColorwayId={selectedId}
          fallbackImageUrl={isDigitalOnly ? data.digitalDesignThumbnailUrl : undefined}
        >
          {badges}
        </CardThumbnail>
      ) : (
        <AssetPreview.Skeleton />
      )}

      {renderedOptions && (
        <Box
          display="flex"
          justifyContent="center"
        >
          <CardColorSwatches
            options={renderedOptions}
            selectedId={selectedId}
            onSelect={handleColorwaySelect}
            variant="overflow"
            onOverflowClick={maybeApplyCallback(!!(approved || isSuperAdmin), handleColorwayOverflowClick)}
            showTooltip
          />
        </Box>
      )}

      <GalleryTile.Content
        brow={brow}
        title={templateToTitle(data)}
        belowTitle={searchScore ? <SearchScoreDebug score={searchScore} /> : undefined}
        label={priceElement}
      >
        {!hideTag && tagVariant && <GalleryTile.Tag variant={tagVariant} />}
      </GalleryTile.Content>
    </GalleryTileWithRef>
  ) : null;
};
